/**
 * @module log adapter
 *
 * Provide a standard interface for logging an array of different kinds of
 * messages. How those messages are reported back to the user can be
 * encapsulated here (e.g., print to console.log, or use a library like pino,
 * or send certain messages to a cloud-based system).
 *
 * This can also be used to prevent certain types of messages from being
 * executed in production (e.g., debug, or trace only print during development).
 */
import { logLevel } from '~/config/settings/app.settings';

// Supported log levels.
const logLevels = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
  silent: Infinity,
};
const appLevel = logLevels[logLevel];

const createLog = (type, msg, data) => {
  const level = logLevels[type] || logLevels.info;
  const isSilent = type === 'silent' || appLevel > level;

  // If current app log-level is higher than log-type's level, or type is "silent",
  // silently store data and do not output to user.
  if (isSilent) {
    // TODO: hook up to cloud log service here, but don't print anything to console
    // await externalAPI.send(log);

    return;
  }

  if (logLevels[logLevel] > logLevels.warn) {
    // @ts-ignore
    const error = new Error(msg, data);

    console.error(error, data);

    return;
  }

  // If made it this far, just output regular log message.
  if (!data) {
    console.log(`${type}: ${msg}`);
  } else {
    console.log(`${type}: ${msg}`, data);
  }
};

const silent = (msg, data) => createLog('silent', msg, data);
const info = (msg, data) => createLog('info', msg, data);
const error = (msg, data) => createLog('error', msg, data);
const trace = (msg, data) => createLog('trace', msg, data);
const debug = (msg, data) => createLog('debug', msg, data);
const warn = (msg, data) => createLog('warn', msg, data);
const fatal = (msg, data) => createLog('fatal', msg, data);

export { trace, debug, info, warn, error, fatal, silent };
